import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import type { ReferenceAvailability } from "@/types/referenceCompound";

/**
 * 참조물질 DB 메뉴를 상단에 **보여도 되는가**.
 *
 * - GET /api/v1/reference-compounds/availability (require_viewer)
 *
 * **판정 방향을 2026-08-22 에 뒤집었다.** 종전에는 "모르면 숨김"이었다 —
 * 로딩 중과 조회 실패를 모두 `available=false` 로 처리했다. 그 결과 일시적인
 * 네트워크 끊김이나 토큰 만료(401) 한 번이면 메뉴가 사라지고, `staleTime`
 * 5분 동안 돌아오지 않았다(운영자 지적: "가끔 상단에 안 보인다").
 *
 * 지금은 **확실히 비어 있을 때만 숨긴다**:
 *
 * * 조회 성공 + core·risk 0 + 활성 표준 자산 없음 → `false` (진짜 빈 상태)
 * * 로딩 중 / 조회 실패 → `true` — 모르는 것은 없는 것이 아니다
 *
 * 이렇게 바꿔도 "빈 탭 노출" 위험은 크지 않다. 목적지 화면이 빈 상태와 오류
 * 상태를 각각 사실대로 말하기 때문이다(2026-08-20 작업). 반대로 메뉴가
 * 사라지면 사용자는 기능이 없어졌다고 읽는다 — 그쪽이 더 나쁜 오해다.
 */
export function useReferenceDataAvailable(): {
  available: boolean;
  isLoading: boolean;
  availability: ReferenceAvailability | undefined;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["reference-compounds-availability"],
    queryFn: () =>
      api
        .get<ReferenceAvailability>("/reference-compounds/availability")
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    // 일시적 실패로 메뉴가 사라지지 않도록 한 번은 다시 시도한다.
    retry: 1,
  });

  // 응답을 실제로 받았을 때만 "비어 있다"고 판정할 수 있다. 그 외(로딩·실패)는
  // 알 수 없는 상태이며, 알 수 없을 때는 감추지 않는다.
  const known = !isLoading && !!data;
  const available = known
    ? data.core_total > 0 || data.risk_total > 0 || data.standard_asset !== null
    : true;

  return { available, isLoading, availability: data };
}
